import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useEventDraft } from '../../../context/useEventDraft.js';
import { useWorkQueueContext } from '../../../context/useWorkQueueContext.js';
import { hitlActionErrorToast } from '../../../utils/hitlActionToast.js';
import { isDecisionSkipped } from '../../../utils/decisionSkipped.js';
import DecisionFooter from '../../DecisionFooter.jsx';
import { DecisionConfirmModal } from '../shared/index.js';
import ApproveSendModal from './ApproveSendModal.jsx';
import { Check, Pencil, Ban, Send } from 'lucide-react';

/**
 * HTL / Client Continuation decision (CT-5096): Approve and send / Edit then send /
 * Block → POST /v1/tasks/hitl/resolve with `decision.status` approved / modified
 * (the edited draft in `modified_args`) / rejected. Same success-gated + pending
 * contract as the other decision footers: the card leaves the inbox only on
 * confirmed success (or a mock row with no `contractId`, which resolves locally).
 *
 * Approve and Block confirm in a modal. Edit follows the prototype instead: the
 * prepared output becomes editable in place and this footer swaps to
 * Cancel / Confirm Send until the reviewer resolves it.
 */
function ContinuationDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { editing, draft, startEdit, cancelEdit } = useEventDraft();
  const { resolveHitlEvent, pendingDecisionIds } = useWorkQueueContext();
  const [mode, setMode] = useState(null); // 'approve' | 'block'
  const output = event.detail?.output;
  const isResolving = pendingDecisionIds?.has(event.id) ?? false;

  const handleCloseMode = useCallback(() => setMode(null), []);

  const handleStartEdit = useCallback(() => {
    startEdit(output?.draft ?? '');
  }, [startEdit, output]);

  const resolve = useCallback(
    async (params, onDone) => {
      try {
        await resolveHitlEvent(event.id, params);
        onDone?.();
      } catch (err) {
        if (isDecisionSkipped(err)) return;
        const { title, message } = hitlActionErrorToast(t, err, 'continuation');
        toast.error({ title, message });
        throw err;
      }
    },
    [resolveHitlEvent, event.id, t, toast]
  );

  const handleApproveSend = useCallback(
    () =>
      resolve({ status: 'approved', auditAction: t('continuation.auditSent') }, () => {
        toast.success({
          title: t('continuation.sentTitle'),
          message: t('continuation.sentMessage', { recipient: output?.recipient ?? '' }),
        });
        setMode(null);
      }),
    [resolve, t, toast, output]
  );

  const handleConfirmSend = useCallback(() => {
    const trimmed = draft.trim();
    return resolve(
      {
        status: 'modified',
        modifiedArgs: { draft: trimmed },
        auditAction: t('continuation.auditSent'),
      },
      () => {
        toast.success({
          title: t('continuation.sentTitle'),
          message: t('continuation.sentWithDraftMessage', {
            recipient: output?.recipient ?? '',
            preview: trimmed.length > 80 ? `${trimmed.slice(0, 80)}…` : trimmed,
          }),
        });
        cancelEdit();
      }
    );
  }, [draft, resolve, t, toast, output, cancelEdit]);

  const handleBlock = useCallback(
    ({ reason }) =>
      resolve(
        {
          status: 'rejected',
          modifiedArgs: { reason },
          auditAction: t('continuation.auditBlocked'),
        },
        () => {
          toast.success({
            title: t('continuation.blockedTitle'),
            message: t('continuation.blockedWithReasonMessage', { reason }),
          });
        }
      ),
    [resolve, t, toast]
  );

  const primaryActions = useMemo(() => {
    if (editing) {
      return [
        {
          id: 'cancel',
          label: t('actions.cancel'),
          size: 'md',
          variant: 'secondary',
          disabled: isResolving,
          onClick: cancelEdit,
        },
        {
          id: 'confirm',
          label: isResolving ? t('hitl.resolving') : t('continuation.confirmSend'),
          size: 'md',
          icon: Send,
          disabled: isResolving || !draft.trim(),
          onClick: handleConfirmSend,
        },
      ];
    }
    return [
      {
        id: 'approve',
        label: t('continuation.approveSend'),
        size: 'md',
        icon: Check,
        disabled: isResolving,
        onClick: () => setMode('approve'),
      },
      {
        id: 'edit',
        label: t('continuation.editSend'),
        size: 'md',
        icon: Pencil,
        variant: 'secondary',
        disabled: isResolving,
        onClick: handleStartEdit,
      },
      {
        id: 'block',
        label: t('continuation.block'),
        size: 'md',
        icon: Ban,
        variant: 'danger',
        disabled: isResolving,
        onClick: () => setMode('block'),
      },
    ];
  }, [editing, draft, isResolving, t, cancelEdit, handleConfirmSend, handleStartEdit]);

  return (
    <>
      <DecisionFooter
        event={event}
        primaryActions={primaryActions}
        hint={
          editing ? t('continuation.editHint', { agent: event.detail?.agent ?? '' }) : undefined
        }
        onSkip={actions.skip}
        onAsk={actions.ask}
        onEscalate={actions.escalate}
        onTrace={actions.trace}
        extraMenuItems={isResolving || editing ? [] : actions.extraMenuItems}
        sticky={actions.sticky}
      />

      <ApproveSendModal
        isOpen={mode === 'approve'}
        event={event}
        isSubmitting={isResolving}
        onConfirm={handleApproveSend}
        onClose={handleCloseMode}
      />

      <DecisionConfirmModal
        isOpen={mode === 'block'}
        onClose={handleCloseMode}
        icon={<Ban size={20} className="text-rose-500" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('continuation.blockTitle')}
        subtitle={t('continuation.blockSubtitle')}
        requireReason
        reasonLabel={t('continuation.blockReasonLabel')}
        reasonPlaceholder={t('continuation.blockReasonPlaceholder')}
        confirmLabel={t('continuation.block')}
        confirmVariant="danger"
        onConfirm={handleBlock}
      />
    </>
  );
}

export default ContinuationDecision;
